import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ContactForm = ({ center, host, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Inquiry',
    message: '',
    preferredContact: 'email',
    interestedServices: []
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const subjects = [
    'General Inquiry',
    'Booking Information',
    'Wellness Programs',
    'EES Technology Questions',
    'Pricing & Packages',
    'Private Sessions',
    'Group Bookings'
  ];

  const services = [
    'Single EES Sessions',
    'Day Passes',
    'Weekly Packages',
    'Meditation Classes',
    'Energy Healing',
    'Wellness Consultations',
    'Group Retreats'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'interestedServices') {
      setFormData(prev => ({
        ...prev,
        interestedServices: checked
          ? [...prev.interestedServices, value]
          : prev.interestedServices.filter(service => service !== value)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    console.log('Contact form submitted:', formData);
    setIsSubmitting(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-modal flex items-center justify-center p-4">
      <div className="bg-surface rounded-lg shadow-elevation-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Contact Host</h2>
            <p className="text-sm text-text-secondary mt-1">
              Get in touch with {host?.name} about {center?.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary-100 rounded-md transition-colors duration-200"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Host Info */}
        <div className="p-6 border-b border-border">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <span className="text-white font-medium">
                {host?.name?.split(' ').map(n => n[0]).join('') || 'H'}
              </span>
            </div>
            <div>
              <h3 className="font-medium text-text-primary">{host?.name}</h3>
              <div className="flex items-center space-x-4 text-sm text-text-secondary">
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} className="text-warning" />
                  <span>{host?.rating} rating</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="MessageCircle" size={14} />
                  <span>Usually responds within 2 hours</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Personal Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Your Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                placeholder="Enter your full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                placeholder="Enter your email"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
                placeholder="(555) 123-4567"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Preferred Contact Method
              </label>
              <select
                name="preferredContact"
                value={formData.preferredContact}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
              >
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="both">Either</option>
              </select>
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Subject
            </label>
            <select
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200"
            >
              {subjects.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>

          {/* Interested Services */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">
              Interested Services (Optional)
            </label>
            <div className="grid grid-cols-2 gap-2">
              {services.map(service => (
                <label key={service} className="flex items-center space-x-2 text-sm">
                  <input
                    type="checkbox"
                    name="interestedServices"
                    value={service}
                    checked={formData.interestedServices.includes(service)}
                    onChange={handleInputChange}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-text-secondary">{service}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Message *
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-md focus:border-primary focus:ring-2 focus:ring-primary-500 transition-all duration-200 resize-none"
              placeholder="Tell us about your wellness goals and any specific questions you have about our EES center..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex items-center space-x-2 bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-700 disabled:bg-primary/50 disabled:cursor-not-allowed transition-all duration-200"
            >
              {isSubmitting && <Icon name="Loader" size={16} className="animate-spin" />}
              <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContactForm;